import Typography from '@components/v2/ui/Typography/Typography';
import { Box, Stack } from '@mui/material';
import { ReactNode } from 'react';
import LangEnum from '../../../../lang/LangEnum';
import { useTranslation } from '../../../translation/TranslationContext';
import { CatalogMediaDimensions } from '../catalogCreationForm.types';
import {
  CATALOG_CREATION_MEDIA_TILE_GAP,
  CATALOG_CREATION_MEDIA_TILE_SIZE,
} from '../catalogCreationMedia.consts';
import { formatCatalogMediaAspectRatio } from '../catalogCreationMedia.utils';

interface CatalogCreationMediaGroupProps {
  label: LangEnum;
  dimensions: CatalogMediaDimensions;
  maxItems: number;
  tileSize?: number;
  isFluid?: boolean;
  children: ReactNode;
}

const CatalogCreationMediaGroup = ({
  label,
  dimensions,
  maxItems,
  tileSize = CATALOG_CREATION_MEDIA_TILE_SIZE,
  isFluid = false,
  children,
}: CatalogCreationMediaGroupProps) => {
  const { translate, formatNumber } = useTranslation();

  const rowWidth = maxItems * tileSize + (maxItems - 1) * CATALOG_CREATION_MEDIA_TILE_GAP;

  const hintLabel =
    maxItems > 1
      ? LangEnum.CATALOGS_MEDIA_MULTIPLE_IMAGES_HINT
      : LangEnum.CATALOGS_MEDIA_SINGLE_IMAGE_HINT;

  const gridColumnsSx = isFluid
    ? { gridTemplateColumns: `repeat(${maxItems}, minmax(0, 1fr))` }
    : {
        gridTemplateColumns: `${tileSize}px`,
        [`@container (min-width: ${rowWidth}px)`]: {
          gridTemplateColumns: `repeat(${maxItems}, ${tileSize}px)`,
        },
      };

  return (
    <Stack sx={{ rowGap: 0.5, minWidth: 0 }}>
      <Typography variant="micro1" sx={{ color: 'text.primary' }}>
        {translate(label)}
      </Typography>

      <Box sx={{ containerType: 'inline-size' }}>
        <Box
          sx={{
            display: 'grid',
            gap: `${CATALOG_CREATION_MEDIA_TILE_GAP}px`,
            ...gridColumnsSx,
          }}
        >
          {children}
        </Box>
      </Box>

      <Typography variant="micro1" sx={{ color: 'text.secondary' }}>
        {translate(hintLabel, {
          maxItems: String(maxItems),
          width: formatNumber(dimensions.width),
          height: formatNumber(dimensions.height),
          ratio: formatCatalogMediaAspectRatio(dimensions),
        })}
      </Typography>
    </Stack>
  );
};

export default CatalogCreationMediaGroup;
